import { toRef, watch, onScopeDispose, toValue, nextTick, useTemplateRef } from 'vue'

import { EVENTS } from '@/utils/events'
import { KEYS } from '@/utils/key-codes'

const { FOCUS_IN, KEYDOWN, POINTER_DOWN } = EVENTS

export const MODAL_CONTENT_NAME = 'modal-content'

/**
 * Stores active focus trap containers and their state.
 *
 * @type {Map<symbol, { isActive: import('vue').Ref<boolean>, contentEl: import('vue').Readonly<import('vue').Ref<HTMLElement | undefined>> }>}
 */
const registry = new Map()
let subscribers = 0

/**
 * Returns all focusable child elements within a given container element.
 *
 * @param {HTMLElement} el - The container element to search for focusable children.
 * @param {boolean} [filterByTabIndex=true] - If true, excludes elements with tabindex="-1" from the results.
 * @returns {HTMLElement[]} Array of focusable child elements.
 */
function focusableChildren(el, filterByTabIndex = true) {
  const targets = [
    'button',
    '[href]',
    'input:not([type="hidden"])',
    'input:not([hidden])',
    'select',
    'textarea',
    'details:not(:has(> summary))',
    'details > summary',
    '[tabindex]',
    '[contenteditable]:not([contenteditable="false"])',
    'audio[controls]',
    'video[controls]'
  ]
    .map(s => `${s}${filterByTabIndex ? ':not([tabindex="-1"])' : ''}:not([disabled], [inert])`)
    .join(', ')

  let elements
  try {
    elements = [...el.querySelectorAll(targets)]
  } catch (err) {
    console.error(String(err))
    return []
  }

  return elements
    .filter(x => !x.closest('[inert]')) // does not have inert parent
    .filter(x => !!x.offsetParent || x.getClientRects().length > 0) // is rendered
    .filter(
      x =>
        !x.parentElement?.closest('details:not([open])') ||
        (x.tagName === 'SUMMARY' && x.parentElement?.tagName === 'DETAILS')
    )
}

/**
 * Handles Tab key navigation to keep focus within the closest active focus trap container.
 * Prevents focus from leaving the trap and cycles focus between first and last focusable elements.
 *
 * @param {KeyboardEvent} e - The keydown event object.
 */
function onKeydown(e) {
  const activeElement = document.activeElement
  if (e.key !== KEYS.TAB || !activeElement) return

  const parentTraps = Array.from(registry.values())
    .filter(
      ({ isActive, contentEl }) => toValue(isActive) && toValue(contentEl)?.contains(activeElement)
    )
    .map(x => toValue(x.contentEl))

  let closestTrap
  let currentParent = activeElement.parentElement
  while (currentParent) {
    if (parentTraps.includes(currentParent)) {
      closestTrap = currentParent
      break
    }
    currentParent = currentParent.parentElement
  }

  if (!closestTrap) return

  const focusable = focusableChildren(closestTrap)
    // excluding elements with negative tabindex
    .filter(x => x.tabIndex >= 0)

  if (!focusable.length) return

  const active = document.activeElement
  if (focusable.length === 1 && focusable[0].contains(active)) {
    e.preventDefault()
    return
  }

  const [firstElement] = focusable
  const lastElement = focusable.at(-1)

  if (e.shiftKey && (active === firstElement || firstElement.contains(active))) {
    e.preventDefault()
    lastElement.focus()
  }

  if (!e.shiftKey && (active === lastElement || lastElement.contains(active))) {
    e.preventDefault()
    firstElement.focus()
  }
}

/**
 * @typedef {Object} FocusTrapOptions
 * @property {boolean} [retainFocus] - Optional. Default true. Determines if focus should remain locked within the container.
 * @property {boolean} [captureFocus] - Optional. Default true. If true, the trap will actively pull focus back if it leaves.
 * @property {import('vue').Ref<boolean>} isActive - Reactive reference indicating if the focus trap is active.
 * @property {import('vue').Readonly<import('vue').Ref<boolean>>} localTop - Readonly reactive reference indicating if this is the top-level trap.
 * @property {string} contentElName - Name of the template ref for the container element of the focus trap.
 */

/**
 * @param {FocusTrapOptions} options - Options including reactive references for active state, top-level status, and container element.
 */
export function useFocusTrap({
  retainFocus = true,
  captureFocus = true,
  isActive,
  localTop,
  contentElName
} = {}) {
  const trapId = Symbol('trap')
  let focusTrapSuppressed = false
  let focusTrapSuppressionTimeout = -1

  const contentEl = useTemplateRef(contentElName)

  async function onPointerdown() {
    focusTrapSuppressed = true
    focusTrapSuppressionTimeout = setTimeout(() => {
      focusTrapSuppressed = false
    }, 100)
  }

  async function captureOnFocus(e) {
    const before = e.relatedTarget
    const after = e.target
    document.removeEventListener(POINTER_DOWN, onPointerdown)
    document.removeEventListener(KEYDOWN, captureOnKeydown)
    await nextTick()
    if (
      toValue(isActive) &&
      !focusTrapSuppressed &&
      before !== after &&
      toValue(contentEl) &&
      // We're the menu without open submenus or overlays
      toValue(localTop) &&
      // It isn't the document or the container body
      ![document, toValue(contentEl)].includes(after) &&
      // It isn't inside the container body
      !toValue(contentEl).contains(after)
    ) {
      const focusable = focusableChildren(toValue(contentEl))
      focusable[0]?.focus()
    }
  }

  function captureOnKeydown(e) {
    if (e.key !== KEYS.TAB) return
    document.removeEventListener(KEYDOWN, captureOnKeydown)
    if (
      toValue(isActive) &&
      toValue(contentEl) &&
      e.target &&
      !toValue(contentEl).contains(e.target)
    ) {
      const allFocusableElements = focusableChildren(document.documentElement)
      if (
        (e.shiftKey && e.target === allFocusableElements.at(0)) ||
        (!e.shiftKey && e.target === allFocusableElements.at(-1))
      ) {
        const focusable = focusableChildren(toValue(contentEl))
        if (focusable.length > 0) {
          e.preventDefault()
          focusable[0].focus()
        }
      }
    }
  }

  const shouldCapture = toRef(() => toValue(isActive) && captureFocus)

  if (subscribers++ < 1) {
    document.addEventListener(KEYDOWN, onKeydown)
  }

  watch(
    () => retainFocus,
    val => {
      if (val) {
        registry.set(trapId, { isActive, contentEl })
      } else {
        registry.delete(trapId)
      }
    },
    { immediate: true }
  )

  watch(
    shouldCapture,
    val => {
      setTimeout(() => {
        if (val) {
          document.addEventListener(POINTER_DOWN, onPointerdown)
          document.addEventListener(FOCUS_IN, captureOnFocus, { once: true })
          document.addEventListener(KEYDOWN, captureOnKeydown)
        } else {
          document.removeEventListener(POINTER_DOWN, onPointerdown)
          document.removeEventListener(FOCUS_IN, captureOnFocus)
          document.removeEventListener(KEYDOWN, captureOnKeydown)
        }
      }, 0)
    },
    { immediate: true }
  )

  onScopeDispose(() => {
    clearTimeout(focusTrapSuppressionTimeout)

    document.removeEventListener(POINTER_DOWN, onPointerdown)
    document.removeEventListener(FOCUS_IN, captureOnFocus)
    document.removeEventListener(KEYDOWN, captureOnKeydown)

    if (--subscribers < 1) {
      document.removeEventListener(KEYDOWN, onKeydown)
    }
  })
}
